"use client";

/**
 * useScrollDrag
 * ─────────────
 * Attaches mouse drag-scroll behaviour to a horizontally scrollable container.
 * Works alongside native touch-scroll (handled by CSS:
 *   -webkit-overflow-scrolling: touch / overscroll-behavior-x: contain).
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useScrollDrag(ref);
 *   return <div ref={ref} className="overflow-x-auto drag-scroll"> ... </div>
 */

import { useEffect, type RefObject } from "react";

export function useScrollDrag(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startX = 0;
    let startScrollLeft = 0;
    let isDragging = false;
    let hasMoved = false; // track whether we actually scrolled

    const onMouseDown = (e: MouseEvent) => {
      // Only react to left button; ignore clicks inside interactive elements
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest("button, a, input, select, textarea, [role='button']")) return;

      isDragging = true;
      hasMoved = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const delta = e.clientX - startX;
      if (Math.abs(delta) > 4) hasMoved = true;
      el.scrollLeft = startScrollLeft - delta;
    };

    const onMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      el.style.cursor = "grab";
      el.style.userSelect = "";
    };

    // Suppress click if we dragged — prevents button activation after scroll
    const onClickCapture = (e: MouseEvent) => {
      if (hasMoved) {
        e.preventDefault();
        e.stopPropagation();
        hasMoved = false;
      }
    };

    // A vertical mouse wheel / trackpad scroll over the row moves it sideways
    // (Gordon 2026-10-30). At either end the page scrolls on as usual; real
    // sideways trackpad gestures keep their native behaviour.
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const atStart = el.scrollLeft <= 0 && e.deltaY < 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 && e.deltaY > 0;
      if (atStart || atEnd) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    el.addEventListener("click", onClickCapture, true);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("wheel", onWheel);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("click", onClickCapture, true);
    };
  }, [ref]);
}
